// Guided tours: which tour runs, at which step, and which tours this visitor has seen. "Seen" is
// remembered on this device only with the "preferences" consent (eb.tour); otherwise it lasts for
// the visit, and withdrawing consent deletes it.
import { create } from "zustand";
import { hasConsent, onConsentChange } from "../ui/consent";

export type TourId = "site" | "editor";

const KEY = "eb.tour";

interface TourState {
  tour: TourId | null;
  step: number;
  seen: Record<TourId, boolean>;
  start: (tour: TourId, step?: number) => void;
  go: (step: number) => void;
  /** End the tour (finished or skipped: either way it counts as seen). */
  stop: () => void;
  /** "Not now" on the welcome prompt. */
  dismiss: (tour: TourId) => void;
}

function load(): Record<TourId, boolean> {
  const none = { site: false, editor: false };
  if (!hasConsent("preferences")) return none;
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as { seen?: Partial<Record<TourId, boolean>> };
    return { site: raw.seen?.site === true, editor: raw.seen?.editor === true };
  } catch {
    return none;
  }
}

function save(seen: Record<TourId, boolean>) {
  if (!hasConsent("preferences")) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ seen }));
  } catch {
    /* storage unavailable */
  }
}

export const useTour = create<TourState>((set, get) => ({
  tour: null,
  step: 0,
  seen: load(),
  start: (tour, step = 0) => set({ tour, step }),
  go: (step) => set({ step }),
  stop: () => {
    const { tour, seen } = get();
    const next = tour ? { ...seen, [tour]: true } : seen;
    save(next);
    set({ tour: null, step: 0, seen: next });
  },
  dismiss: (tour) => {
    const next = { ...get().seen, [tour]: true };
    save(next);
    set({ seen: next });
  },
}));

onConsentChange((c) => {
  if (c.preferences) save(useTour.getState().seen);
  else
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* storage unavailable */
    }
});
